import clsx from "clsx";
import React from "react";
import {
  Button,
  ButtonProps,
  Checkbox,
  CheckboxProps,
  FormGroup,
  FormControlLabel,
  Popover,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ArrowDropDown } from "@mui/icons-material";

import { humanlyEnumerate } from "../../utils/component";

import { defaultAssetTypes, LibraryAssetType } from "./constants";

const filterAssetTypes = defaultAssetTypes;
const getContentType = (assetType: LibraryAssetType) =>
  assetType.toLocaleLowerCase();

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.text.secondary,
    fontSize: 15,
  },
  popover: {
    zIndex: "99999 !important" as any,
  },
  paper: {
    borderRadius: theme.spacing(1.5),
    minWidth: theme.spacing(32),
    padding: theme.spacing(2, 0),

    "& > :not($divider)": {
      margin: theme.spacing(0, 2),
    },
  },
  divider: {
    margin: theme.spacing(1.5, 0),
  },
  label: {
    color: theme.palette.common.black,
    fontWeight: 500,
    textTransform: "capitalize",
  },
  header: {
    color: theme.palette.text.secondary,
    fontSize: 14,
    fontWeight: 500,
  },
}));

export type LibraryAssetFilters = Record<LibraryAssetType, boolean>;

export const defaultLibraryAssetFilters: LibraryAssetFilters = {
  [LibraryAssetType.IMAGE]: true,
  [LibraryAssetType.VIDEO]: true,
  [LibraryAssetType.AUDIO]: true,
  [LibraryAssetType.FILE]: true,
};

export interface ComponentLibraryAssetFilterProps
  extends Omit<ButtonProps, "onChange"> {
  filters?: LibraryAssetFilters;
  onChange?: (filters: LibraryAssetFilters) => void;
}

export function ComponentLibraryAssetFilter(
  props: ComponentLibraryAssetFilterProps,
) {
  const {
    className,
    onChange,
    filters = defaultLibraryAssetFilters,
    ...other
  } = props;
  const s = useStyles();
  const ref = React.useRef<HTMLButtonElement>();
  const [open, setOpen] = React.useState(false);

  const handleOpen = React.useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const handleChange: CheckboxProps["onChange"] = React.useCallback(
    (event, checked) => {
      if (onChange) {
        const newFilter = {
          ...filters,
          [event.target.value]: checked,
        };
        const empty = filterAssetTypes.every((type) => !newFilter[type]);

        if (!empty) {
          onChange(newFilter);
        }
      }
    },
    [filters, onChange],
  );

  const contentTypesTexts = filterAssetTypes
    .filter((type) => filters[type])
    .map((type) => `${getContentType(type)}s`);

  const filterStatusText = [
    contentTypesTexts.length === defaultAssetTypes.length
      ? ["All assets"]
      : humanlyEnumerate(contentTypesTexts),
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      <Button
        ref={ref}
        className={clsx(s.root, className)}
        onClick={handleOpen}
        {...other}
      >
        Filter by: {filterStatusText}
        <ArrowDropDown />
      </Button>
      <Popover
        open={open}
        anchorEl={ref.current}
        onClose={handleClose}
        classes={{ root: s.popover, paper: s.paper }}
        anchorOrigin={{
          horizontal: "left",
          vertical: "bottom",
        }}
        transformOrigin={{
          horizontal: "left",
          vertical: "top",
        }}
      >
        <Typography className={s.header} variant="body2">
          Filter by
        </Typography>
        {defaultAssetTypes.map((type) => (
          <FormGroup key={type}>
            <FormControlLabel
              control={
                <Checkbox
                  color="primary"
                  value={type}
                  checked={filters[type]}
                  onChange={handleChange}
                />
              }
              label={
                <Typography variant="body1" className={s.label}>
                  {getContentType(type)}s
                </Typography>
              }
            />
          </FormGroup>
        ))}
      </Popover>
    </>
  );
}
